/**
 * Shared timezone-aware time formatters for the Calendar Month View.
 *
 * Single source of truth — used by:
 *   - `EventViewModal` (top + bottom datetime lines)
 *   - `EventChip` (grid cell HH:MM label)
 *   - `EventRow` (grid row HH:MM)
 *   - `formatEventTooltip` (chip hover tooltip)
 *
 * Every formatter accepts an Olson timezone string and applies it via
 * `Intl.DateTimeFormat({ timeZone })`. `computeOffsetString` is DST-correct:
 * it evaluates the offset AT the supplied date so winter vs summer NY
 * tz returns `-05:00` vs `-04:00` respectively.
 *
 * @module timeFormatters
 */

/**
 * Format unix epoch seconds as `"<Month> <Day>, <Year> HH:MM"` in the
 * supplied Olson timezone. Falls back to browser local when `timeZone` is
 * omitted (UI guarantees a value — fallback exists for defense-in-depth).
 *
 * @param {number|string} ts Unix epoch seconds.
 * @param {string} [timeZone] e.g. `America/Adak`.
 */
export function formatDateTime(ts, timeZone) {
	if (!ts) return '—';
	const num = Number(ts);
	if (!Number.isFinite(num)) return String(ts);
	const d = new Date(num * 1000);
	const opts = { month: 'long', day: 'numeric', year: 'numeric' };
	const tOpts = { hour: '2-digit', minute: '2-digit', hour12: false };
	if (timeZone) {
		opts.timeZone = timeZone;
		tOpts.timeZone = timeZone;
	}
	const date = d.toLocaleDateString('en-US', opts);
	const time = d.toLocaleTimeString('en-US', tOpts);
	return `${date} ${time}`;
}

/**
 * Compute UTC offset (`±HH:MM`) for a tz at a Date. DST-correct.
 * Handles half-hour zones (`Asia/Kolkata` → `+05:30`) and 45-min zones.
 *
 * @param {Date} date
 * @param {string} timeZone
 * @returns {string} e.g. `+05:30`, `-04:00`, `+00:00`.
 */
export function computeOffsetString(date, timeZone) {
	// PERF: building an Intl.DateTimeFormat costs ~0.2 ms, and the calendars call
	// this for every event edge on every render — a busy day view spent 125 ms of
	// every lane rebuild here. Reuse one formatter per zone, and one answer per
	// 15-minute slot: every real UTC-offset change lands on a 15-minute boundary
	// (offsets are whole quarter-hours), so a slot never straddles a change.
	const ms = date instanceof Date ? date.getTime() : NaN;
	const slotKey = Number.isFinite(ms) ? `${timeZone}|${Math.floor(ms / OFFSET_SLOT_MS)}` : null;
	if (slotKey !== null && offsetBySlot.has(slotKey)) return offsetBySlot.get(slotKey);
	const result = computeOffsetStringUncached(date, timeZone);
	if (slotKey !== null) {
		if (offsetBySlot.size >= OFFSET_CACHE_MAX) offsetBySlot.clear();
		offsetBySlot.set(slotKey, result);
	}
	return result;
}

const OFFSET_SLOT_MS = 15 * 60 * 1000;
const OFFSET_CACHE_MAX = 20000;
const offsetBySlot = new Map();
const offsetFormatters = new Map();

function offsetFormatterFor(timeZone) {
	const key = timeZone === undefined ? '' : String(timeZone);
	if (!offsetFormatters.has(key)) {
		let fmt = null;
		try {
			fmt = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' });
		} catch (_) {
			fmt = null; // invalid zone — remembered so it isn't rebuilt per call
		}
		offsetFormatters.set(key, fmt);
	}
	return offsetFormatters.get(key);
}

function computeOffsetStringUncached(date, timeZone) {
	try {
		const fmt = offsetFormatterFor(timeZone);
		if (!fmt) return '+00:00';
		const parts = fmt.formatToParts(date);
		const tzPart = parts.find(p => p.type === 'timeZoneName')?.value || 'GMT';
		const match = tzPart.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
		if (!match) return '+00:00';
		const sign = match[1].startsWith('-') ? '-' : '+';
		const hours = String(Math.abs(parseInt(match[1], 10))).padStart(2, '0');
		const minutes = match[2] || '00';
		return `${sign}${hours}:${minutes}`;
	} catch (_) {
		return '+00:00';
	}
}

/**
 * DST-correct UTC offset (ms, positive east) for `tz` at the given instant.
 *
 * @param {number} unixSeconds Unix epoch seconds.
 * @param {string} [tz] IANA zone id; falls back to UTC.
 * @returns {number} Offset in milliseconds, e.g. `-14400000` for `-04:00`.
 */
export function tzOffsetMs(unixSeconds, tz) {
	const off = computeOffsetString(new Date(unixSeconds * 1000), tz || 'UTC');
	const m = /([+-])(\d{2}):(\d{2})/.exec(off);
	if (!m) return 0;
	const sign = m[1] === '-' ? -1 : 1;
	return sign * (Number(m[2]) * 60 + Number(m[3])) * 60 * 1000;
}

/**
 * `ABBR City ±HH:MM` for a zone at a date (DST-aware), e.g.
 * `EDT New York -04:00` — the bracket text of a location label.
 *
 * @param {Date} date
 * @param {string} timeZone IANA id.
 * @returns {string} `''` when nothing can be resolved.
 */
export function formatTzMeta(date, timeZone) {
	if (!timeZone) return '';
	let abbr = '';
	try {
		const fmt = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' });
		abbr = fmt.formatToParts(date).find(p => p.type === 'timeZoneName')?.value || '';
	} catch (_) {
		abbr = '';
	}
	const segs = String(timeZone).split('/');
	const city = (segs[segs.length - 1] || '').replace(/_/g, ' ');
	return [abbr, city, computeOffsetString(date, timeZone)].filter(Boolean).join(' ');
}

// UTC itself under any of its IANA names — the only zone that is Zulu time.
const UTC_ZONE_RE = /^(Etc\/)?(UTC|UCT|Universal|Zulu|Greenwich|GMT([+-]?0)?)$/i;

/**
 * Format unix epoch seconds in the supplied tz, annotated with that zone
 * (legacy parity): `"May 20, 2026 02:45 [EDT New York -04:00]"`, and
 * `"May 20, 2026 06:45 [UTC Zulu time +00:00]"` only when the zone is UTC.
 *
 * @param {number|string} ts Unix epoch seconds.
 * @param {string} [timeZone] Falls back to UTC.
 */
export function formatDateTimeWithOffset(ts, timeZone) {
	if (!ts) return '';
	const num = Number(ts);
	if (!Number.isFinite(num)) return '';
	const d = new Date(num * 1000);
	const tz = timeZone || 'UTC';
	const opts = { month: 'long', day: 'numeric', year: 'numeric', timeZone: tz };
	const tOpts = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz };
	const date = d.toLocaleDateString('en-US', opts);
	const time = d.toLocaleTimeString('en-US', tOpts);
	const meta = UTC_ZONE_RE.test(tz)
		? `UTC Zulu time ${computeOffsetString(d, tz)}`
		: formatTzMeta(d, tz);
	return `${date} ${time} [${meta}]`;
}

/**
 * Format unix epoch seconds as a short `HH:MM` label in the supplied tz.
 * Used for grid chips / rows where we only show the time component.
 *
 * @param {number|string} ts Unix epoch seconds.
 * @param {string} [timeZone] Falls back to browser local when omitted.
 * @returns {string} `HH:MM` (24-hour) or `''` for invalid input.
 */
export function formatTimeShort(ts, timeZone) {
	if (ts == null || ts === '') return '';
	const num = Number(ts);
	if (!Number.isFinite(num)) return '';
	const d = new Date(num * 1000);
	if (Number.isNaN(d.getTime())) return '';
	const tOpts = { hour: '2-digit', minute: '2-digit', hour12: false };
	if (timeZone) tOpts.timeZone = timeZone;
	return d.toLocaleTimeString('en-US', tOpts);
}
